// Resilient transport for the idempotent cart-page/drawer fetches (section
// re-render, /cart/change.js quantity, /cart/update.js note). Routes them through
// NttUtils.fetchWithRetry so transient iOS Facebook/Instagram in-app-webview
// aborts recover instead of dead-ending the cart. Same Promise<Response> contract
// as fetch, so cart.js's existing .then/.catch stay untouched.

// Additive hook point for the entitlement observer (assets/cart-entitlement.js):
// after a *successful* mutating cart response (add/change/update/clear), notify
// any registered listener so it can reconcile freebie entitlement. Wired as a
// plain callback registry (not an import) so this file has zero build-time
// dependency on cart-entitlement.js — which may not even be loaded — and vice
// versa. This never changes cartFetch's return value, timing, or error
// behavior: it observes the same promise cart.js already gets back, it does
// not wrap or replace it.
const cartMutationListeners = [];

function onCartMutation(listener) {
  if (typeof listener === 'function') cartMutationListeners.push(listener);
}

// Base paths, not `.js`-suffixed: Shopify's `routes.cart_*_url` values are
// unsuffixed (`/cart/change`) and cart.js fetches them verbatim, while other
// theme code uses the `.js` forms — both must count as mutations or the
// listeners (the entitlement belt's in-page trigger) miss cart-page removals.
const MUTATING_CART_ENDPOINTS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

function isMutatingCartUrl(url) {
  const s = String(url || '');
  return MUTATING_CART_ENDPOINTS.some((endpoint) => s.indexOf(endpoint) !== -1);
}

function notifyCartMutationListeners(url, response) {
  if (!response || !response.ok || !isMutatingCartUrl(url)) return;
  cartMutationListeners.forEach((listener) => {
    try {
      listener(url, response);
    } catch (error) {
      // A listener's own error must never surface as a cart-fetch failure.
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('cart mutation listener failed', error);
      }
    }
  });
}

function cartFetch(url, options, deps) {
  const d = deps || {};
  const retry =
    d.fetchWithRetry ||
    (typeof window !== 'undefined' && window.NttUtils && window.NttUtils.fetchWithRetry);
  // Cart ops are interactive and idempotent (absolute quantity / overwrite note /
  // GET re-render), so retry safely but with a low count — surface a hard failure
  // after one retry rather than stalling two backoffs like the add path (retries:2).
  const cfg = Object.assign({ retries: 1, backoffMs: 300, step: 'cart' }, d.config);
  const resultPromise = retry ? retry(url, options, cfg) : fetch(url, options);
  // Fire-and-forget: observe the same promise cart.js's caller already holds,
  // without altering what it resolves/rejects to. The second (onRejected) arm
  // exists only so a failed cart op doesn't produce an unhandled-rejection
  // warning on this side-branch — it does not touch the returned promise.
  resultPromise.then(
    (response) => notifyCartMutationListeners(url, response),
    () => {},
  );
  return resultPromise;
}

// Browser-only: expose cartFetch for cart.js. Guarded so require() under bare Node
// (no window) doesn't throw; consistent with NttUtils.js.
if (typeof window !== 'undefined') {
  window.CartResilience = window.CartResilience ?? {};
  window.CartResilience.cartFetch = cartFetch;
  window.CartResilience.onCartMutation = onCartMutation;
}

// Node-only test export. Skipped under <script> in the theme (process undefined).
if (
  typeof module === 'object' && module !== null &&
  typeof module.exports === 'object' &&
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null
) {
  module.exports = { cartFetch, onCartMutation, isMutatingCartUrl };
}
